import {
    ActiveSelection,
    Circle,
    Control,
    controlsUtils,
    Ellipse,
    FabricObject,
    FabricText,
    InteractiveFabricObject,
    Point,
    Polyline,
    TPointerEvent,
    Transform,
    util
} from "fabric";
import {MIN_WALL_LENGTH} from "./constants";
import {normalizeDefaultDimension} from "./snap-geometry";

/**
 * Setup Fabric.js object defaults (corner styling, controls, etc.)
 * Call this once after creating the canvas.
 */
export const setupFabricDefaults = () => {
    // Set center origin as default for all objects (Fabric.js v6 recommendation)
    FabricObject.ownDefaults.originX = 'center';
    FabricObject.ownDefaults.originY = 'center';

    // Interactive object defaults
    InteractiveFabricObject.ownDefaults = {
        ...InteractiveFabricObject.ownDefaults,
        cornerColor: '#4285f4',
        cornerStrokeColor: '#fff',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: '#4285f4',
        borderScaleFactor: 2,
        padding: 4,
    };

    // Create controls with circle rendering (v6 recommended approach instead of deprecated cornerStyle)
    const allControls = controlsUtils.createObjectDefaultControls();
    Object.values(allControls).forEach(control => {
        control.render = controlsUtils.renderCircleControl;
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {mtr: _mtr, ...defaultControls} = allControls;
    InteractiveFabricObject.ownDefaults.controls = defaultControls;

    // Corner-only controls (already have circle render from above)
    const {tl, tr, bl, br} = defaultControls;
    const cornerOnlyControls = {tl, tr, bl, br};

    ActiveSelection.ownDefaults = {
        ...ActiveSelection.ownDefaults,
        controls: cornerOnlyControls,
        lockRotation: true,
    };

    FabricText.ownDefaults = {
        ...FabricText.ownDefaults,
        controls: cornerOnlyControls,
    };

    Circle.ownDefaults = {
        ...Circle.ownDefaults,
        controls: cornerOnlyControls,
    };

    // Ellipse uses all controls (including middle handles) for independent width/height scaling
    Ellipse.ownDefaults = {
        ...Ellipse.ownDefaults,
        controls: defaultControls,
    };

    // Polyline point controls
    const createPolylinePointControl = (pointIndex: 'start' | 'end') => {
        return new Control({
            positionHandler: function (_dim, _finalMatrix, fabricObject) {
                const poly = fabricObject as Polyline;
                const points = poly.points;
                if (!points || points.length < 2) {
                    return new Point(0, 0);
                }

                const idx = pointIndex === 'start' ? 0 : points.length - 1;
                const point = points[idx];

                // Use vpt × calcTransformMatrix() — NOT the passed finalMatrix,
                // which strips object scale and is meant for dim-based controls.
                return new Point(point.x, point.y)
                    .subtract(poly.pathOffset)
                    .transform(
                        util.multiplyTransformMatrices(
                            poly.getViewportTransform(),
                            poly.calcTransformMatrix()
                        )
                    );
            },

            actionHandler: function (eventData: TPointerEvent, transform: Transform, x: number, y: number) {
                const poly = transform.target as Polyline;
                const points = poly.points;
                if (!points || points.length < 2) return false;

                const idx = pointIndex === 'start' ? 0 : points.length - 1;
                const otherIdx = pointIndex === 'start' ? points.length - 1 : 0;
                const currentLeft = poly.left ?? 0;
                const currentTop = poly.top ?? 0;

                // x,y are in scene space (already viewport-adjusted by Fabric.js)
                // so only invert the object's own transform matrix
                const invertedMatrix = util.invertTransform(poly.calcTransformMatrix());
                let localPoint = util.transformPoint({x, y}, invertedMatrix);

                // Angle snapping by default, disabled when holding Shift, Ctrl, or Meta key
                const e = eventData as MouseEvent;
                if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
                    const SNAP_ANGLE = 15; // degrees
                    const otherPoint = points[otherIdx];

                    // Calculate in pathOffset-adjusted coordinates
                    const dragX = localPoint.x + poly.pathOffset.x;
                    const dragY = localPoint.y + poly.pathOffset.y;

                    const dx = dragX - otherPoint.x;
                    const dy = dragY - otherPoint.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance > 0) {
                        // Get angle in degrees and snap to nearest increment
                        let angle = Math.atan2(dy, dx) * (180 / Math.PI);
                        angle = Math.round(angle / SNAP_ANGLE) * SNAP_ANGLE;
                        const radians = angle * (Math.PI / 180);

                        // Calculate snapped position
                        const snappedX = otherPoint.x + distance * Math.cos(radians);
                        const snappedY = otherPoint.y + distance * Math.sin(radians);

                        localPoint = new Point(
                            snappedX - poly.pathOffset.x,
                            snappedY - poly.pathOffset.y
                        );
                    }
                }

                const otherPoint = points[otherIdx];
                const dragX = localPoint.x + poly.pathOffset.x;
                const dragY = localPoint.y + poly.pathOffset.y;
                const dx = dragX - otherPoint.x;
                const dy = dragY - otherPoint.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                // Walls resize by endpoints, so their min-size rule lives here.
                if (distance < MIN_WALL_LENGTH) {
                    const currentPoint = points[idx];
                    const fallbackDx = currentPoint.x - otherPoint.x;
                    const fallbackDy = currentPoint.y - otherPoint.y;
                    const fallbackDistance = Math.sqrt(fallbackDx * fallbackDx + fallbackDy * fallbackDy);
                    const unitX = distance > 0 ? dx / distance : fallbackDistance > 0 ? fallbackDx / fallbackDistance : 1;
                    const unitY = distance > 0 ? dy / distance : fallbackDistance > 0 ? fallbackDy / fallbackDistance : 0;

                    localPoint = new Point(
                        otherPoint.x + unitX * MIN_WALL_LENGTH - poly.pathOffset.x,
                        otherPoint.y + unitY * MIN_WALL_LENGTH - poly.pathOffset.y
                    );
                }

                points[idx] = {
                    x: localPoint.x + poly.pathOffset.x,
                    y: localPoint.y + poly.pathOffset.y,
                };

                if (poly.floorPlanItem?.element) {
                    (poly.floorPlanItem.element.canvas as any).points = [...points];
                }

                const oldPathOffset = {x: poly.pathOffset.x, y: poly.pathOffset.y};
                poly.setDimensions();

                poly.set({
                    left: currentLeft + (poly.pathOffset.x - oldPathOffset.x),
                    top: currentTop + (poly.pathOffset.y - oldPathOffset.y),
                });
                poly.setCoords();

                return true;
            },

            cursorStyle: 'pointer',
            actionName: 'modifyPolyline',

            render: function (ctx, left, top) {
                ctx.save();
                ctx.fillStyle = '#4285f4';
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(left, top, 6, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            },
        });
    };

    Polyline.ownDefaults = {
        ...Polyline.ownDefaults,
        controls: {
            start: createPolylinePointControl('start'),
            end: createPolylinePointControl('end'),
        },
        hasBorders: true,
    };
};


export const FABRIC_DEFAULTS = {
    angle: 0,
    opacity: 1,
    scaleX: 1,
    scaleY: 1,
    skewX: 0,
    skewY: 0,
    strokeDashArray: null as number[] | null,
    strokeDashOffset: 0,

    fill: '#D0D0D0',
    stroke: '#999999',
    strokeWidth: 2,

    table: {
        width: normalizeDefaultDimension(140),
        height: normalizeDefaultDimension(90),
        circleRadius: normalizeDefaultDimension(130) / 2,
        shape: 'rect' as const,
        cornerRadius: 8,

        fill: '#FFFFFF',
        stroke: '#CCCCCC',
        strokeWidth: 2,
        strokeDashArray: null as number[] | null,

        textColor: '#333333',

        seats: {
            size: 10,
            offset: 12,
            circleSlots: 8,
            edges: {
                top: 1,
                right: 1,
                bottom: 1,
                left: 1,
            },
            circle: {
                startAngleDeg: 0,
                sweepAngleDeg: 360,
                includeEndpoints: true,
                snapStepDeg: 15,
            },
        },
    },

    seat: {
        fillActive: '#4CAF50',
        strokeActive: '#388E3C',
        fillInactive: '#E0E0E0',
        strokeInactive: '#BDBDBD',
    },

    shape: {
        width: normalizeDefaultDimension(100),
        height: normalizeDefaultDimension(100),
        ellipseWidth: normalizeDefaultDimension(120),
        ellipseHeight: normalizeDefaultDimension(80),
        shape: 'rect' as const,
        fill: '#FFFFFF',
        stroke: '#CCCCCC',
        strokeWidth: 2,
        strokeDashArray: null as number[] | null,
        cornerRadius: 0,
    },

    text: {
        text: 'Text',
        fill: '#333333',
        fontSize: 24,
        fontFamily: 'sans-serif',
        fontWeight: 'normal' as const,
        fontStyle: 'normal' as const,
        underline: false,
        align: 'center' as const,
    },

    wall: {
        length: normalizeDefaultDimension(100),
        stroke: '#424242',
        strokeWidth: 8,
        strokeDashArray: null as number[] | null,
        opacity: 1,
        points: [
            {x: 0, y: 0},
            {x: 100, y: 0},
        ],
    },
};
